import { useState } from 'react';
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  FileSignature,
  FileText,
  MessageSquare,
  Search,
  User,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetAllSellerContractsQuery } from '@/apis/extended/sellerAPI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { parseDateOnly } from '@/utils/dateOnly';

const SellerdashboardPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { data: contracts } = useGetAllSellerContractsQuery(sellerId ?? '', {
    skip: !sellerId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return (
          <Badge
            variant="secondary"
            className="bg-[var(--surface-container-high)] text-[var(--on-surface-variant)]"
          >
            Draft
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-[var(--primary-navy)] text-white">
            Submitted
          </Badge>
        );
      case 'negotiating':
        return <Badge className="bg-orange-500 text-white">Negotiating</Badge>;
      case 'negotiations_complete':
        return (
          <Badge className="bg-blue-600 text-white">
            Negotiations Complete
          </Badge>
        );
      case 'awaiting_signatures':
        return (
          <Badge className="bg-yellow-500 text-black">
            Awaiting Signatures
          </Badge>
        );
      case 'awaiting_buyer_signature':
        return (
          <Badge className="bg-yellow-500 text-black">
            Awaiting Buyer Signature
          </Badge>
        );
      case 'awaiting_seller_signature':
        return (
          <Badge className="bg-yellow-600 text-white">
            Your Signature Needed
          </Badge>
        );
      case 'ratified':
        return <Badge className="bg-green-600 text-white">Ratified</Badge>;
      case 'closed':
        return <Badge className="bg-gray-600 text-white">Closed</Badge>;
      case 'voided':
        return <Badge className="bg-red-600 text-white">Voided</Badge>;
      case 'accepted':
        return (
          <Badge variant="trust" className="text-[var(--tertiary-fixed)]">
            Accepted
          </Badge>
        );
      case 'rejected':
        return <Badge className="bg-[#f50057] text-white">Rejected</Badge>;
      case 'expired':
        return (
          <Badge variant="outline" className="text-[var(--on-surface-variant)]">
            Expired
          </Badge>
        );
      default:
        return (
          <Badge
            variant="secondary"
            className="bg-[var(--surface-container-high)] text-[var(--on-surface-variant)]"
          >
            {status}
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] text-[var(--on-surface)]">
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-[var(--on-surface)] mb-2">
              Seller Dashboard
            </h1>
            <p className="text-[var(--on-surface-variant)]">
              Manage and track your real estate contracts efficiently
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <FileText className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Active Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                {contracts?.length || 0}
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Offers in progress
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <User className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Total Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                {contracts?.length || 0}
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Total Contracts in progress
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <Calendar className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                0
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Deadlines this week
              </p>
            </CardContent>
          </Card>
        </div>
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--on-surface-variant)]" />
            <Input
              placeholder="Search contracts by address or ID..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <Tabs defaultValue="active">
          <TabsList className="mb-6 bg-[var(--surface-container-low)]">
            <TabsTrigger
              value="active"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Completed
            </TabsTrigger>
            <TabsTrigger
              value="drafts"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Drafts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {contracts?.map((contract: any) => (
              <Card
                key={contract._id}
                className="mb-4 bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-medium text-lg text-[var(--on-surface)]">
                        Property Address
                      </h3>
                      <p className="text-[var(--on-surface-variant)]">
                        {contract?.propertyDetails?.name || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center">
                      {getStatusBadge(
                        contract?.contractDetails?.contractStatus || 'draft'
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 justify-between items-center mb-4">
                    <div>
                      <p className="text-sm text-[var(--on-surface-variant)]">
                        Offer Amount
                      </p>
                      <p className="font-medium text-[var(--on-surface)]">
                        $
                        {contract.contractDetails?.offerSpecifics?.offerPrice ||
                          '0'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[var(--on-surface-variant)]">
                        Offer Date
                      </p>
                      <p className="font-medium text-[var(--on-surface)]">
                        {contract.createdAt
                          ? new Date(contract.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[var(--on-surface-variant)]">
                        Closing Date
                      </p>
                      <p className="font-medium text-[var(--on-surface)]">
                        {contract.contractDetails?.offerSpecifics?.closingDate
                          ? parseDateOnly(
                              contract.contractDetails?.offerSpecifics
                                ?.closingDate
                            )?.toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[var(--on-surface-variant)]">
                        Financing Commitment
                      </p>
                      <p className="font-medium capitalize text-[var(--on-surface)]">
                        {contract.contractDetails?.offerSpecifics
                          ?.financingCommittmentDate
                          ? parseDateOnly(
                              contract.contractDetails?.offerSpecifics
                                ?.financingCommittmentDate
                            )?.toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[var(--on-surface-variant)]">
                        Financing Type
                      </p>
                      <p className="font-medium capitalize text-[var(--on-surface)]">
                        {contract.contractDetails?.offerSpecifics?.offerType ||
                          'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Signature Alert and Button */}
                  {(contract.contractDetails?.contractStatus ===
                    'awaiting_signatures' ||
                    contract.contractDetails?.contractStatus ===
                      'awaiting_seller_signature') &&
                    contract.contractDetails?.buyerSign?.isStored &&
                    !contract.contractDetails?.sellerSign?.isStored && (
                      <Alert className="mb-4 bg-yellow-900/20 border-yellow-600">
                        <FileSignature className="h-4 w-4 text-yellow-500" />
                        <AlertTitle className="text-yellow-500">
                          Signature Required
                        </AlertTitle>
                        <AlertDescription className="text-yellow-200">
                          This contract has been finalized and is ready for your
                          signature.
                          {contract.contractDetails?.buyerSign?.isStored && (
                            <span> The buyer has already signed.</span>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end flex-wrap">
                    {/* Negotiate button — shown for active negotiation states */}
                    {(contract.contractDetails?.contractStatus === 'draft' ||
                      contract.contractDetails?.contractStatus ===
                        'submitted' ||
                      contract.contractDetails?.contractStatus ===
                        'negotiating' ||
                      contract.contractDetails?.contractStatus ===
                        'negotiations_complete') && (
                      <Button
                        variant="outline"
                        className="border-[var(--primary-navy)] text-[var(--primary-navy)] hover:bg-[var(--primary-navy)] hover:text-white"
                        onClick={() =>
                          navigate(
                            `/seller/negotiate/${contract.contractDetails?._id}`
                          )
                        }
                      >
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Negotiate
                      </Button>
                    )}
                    {/* Sign button — shown when awaiting seller signature */}
                    {(contract.contractDetails?.contractStatus ===
                      'awaiting_signatures' ||
                      contract.contractDetails?.contractStatus ===
                        'awaiting_seller_signature') &&
                      contract.contractDetails?.buyerSign?.isStored &&
                      !contract.contractDetails?.sellerSign?.isStored && (
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() =>
                            navigate(
                              `/seller/contract/${contract.contractDetails?._id}/sign`
                            )
                          }
                        >
                          <FileSignature className="mr-2 h-4 w-4" />
                          Sign Contract
                        </Button>
                      )}
                    {/* View contract — always available */}
                    <Button
                      variant="ghost"
                      className="text-[var(--on-surface-variant)]"
                      onClick={() =>
                        navigate(
                          `/view-contract/${contract.contractDetails?._id}`
                        )
                      }
                    >
                      <ArrowRight className="mr-1 h-4 w-4" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="completed">
            <div className="text-center py-12">
              <div className="rounded-lg bg-[var(--surface-container-low)] p-8">
                <CheckCircle className="h-12 w-12 text-[#0ed145] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--on-surface)] mb-2">
                  No completed contracts
                </h3>
                <p className="text-[var(--on-surface-variant)]">
                  Completed contracts will appear here once finalized.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="drafts">
            <div className="text-center py-12">
              <div className="rounded-lg bg-[var(--surface-container-low)] p-8">
                <FileText className="h-12 w-12 text-[var(--on-surface-variant)] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--on-surface)] mb-2">
                  No draft contracts
                </h3>
                <p className="text-[var(--on-surface-variant)]">
                  Start creating your first contract draft.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SellerdashboardPage;
